import useSafeState from "../utils/safe-state";
import {
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    Spin,
    Radio,
    Space,
    Tooltip,
    Divider,
    DatePicker,
    List,
    Typography
} from "antd";
import {DeleteOutlined, InfoCircleTwoTone, LeftOutlined} from "@ant-design/icons";
import React, {useEffect, useState} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import ButtonWithIcon from "../components/buttons/button-with-icon";
import TextArea from "antd/es/input/TextArea";
import moment from "moment/moment";

interface ItemDataFormProps {
    data?: any;
    onClose?: () => void;
    onBack?: () => void;
}

const ItemDataForm = (props: ItemDataFormProps) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);
    const [countries, setCountries] = useSafeState<string[]>([]);
    const itemId = props.data && props.data.itemId;
    const [finProdGoodsMixing, setFinProdGoodsMixing] = useSafeState(false);
    const [rawMatGoodsMixing, setRawMatGoodsMixing] = useSafeState(false);
    const [unusualTradeFlows, setUnusualTradeFlows] = useSafeState(false);
    const [isSynthetic, setIsSynthetic] = useSafeState(false);


    useEffect(() => {
        if (itemId) {
            fetchData();
        }
        fetchCountries();
    }, [itemId]);

    const fetchCountries = () => {
        if (axiosInstance.current != null) {
            axiosInstance.current
                .get('/resource/countries/get-all')
                .then(response => {
                    setCountries(response.data);
                })
                .catch(function (error: any) {
                    console.log(error);
                });
        }
    };

    const fetchData = () => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .get(`/resource/item-data/get-data?itemId=${itemId}`)
                .then(response => {
                    const data = response.data;

                    setIsSynthetic(data.checkedSyntheticRubber === 'true');
                    setFinProdGoodsMixing(data.goodsMixing === '1');
                    setRawMatGoodsMixing(data.rawMaterialGoodsMixing === '1');
                    setUnusualTradeFlows(data.unusualTradeFlows === '1');
                    if(data.originOfRelevantPart){
                        setDataOriginOfRelevantPart(
                            data.originOfRelevantPart
                                .split(",")
                                .map((item: string) => item.trim())
                        );
                    }
                    if(data.originOfRawMat){
                        setDataOriginOfRawMat(
                            data.originOfRawMat
                                .split(",")
                                .map((item: string) => item.trim())
                        );
                    }
                    if(data.countryOfProductionOfRelevantCommodity){
                        setDataCountryOfProductionOfRelevantCommodity(
                            data.countryOfProductionOfRelevantCommodity
                                .split(",")
                                .map((item: string) => item.trim())
                        );
                    }
                    if(data.countryOfProductionOfRelevantPartFinishedProduct){
                        setDataCountryOfProductionOfRelevantPartFinishedProduct(
                            data.countryOfProductionOfRelevantPartFinishedProduct
                                .split(",")
                                .map((item: string) => item.trim())
                        );
                    }

                    const formattedData = {
                        ...data,
                        checkedSyntheticRubber: data.checkedSyntheticRubber === 'true',
                        checkedLegacyStock: data.checkedLegacyStock === 'true',
                        goodsMixing: data.goodsMixing ? Number(data.goodsMixing) : undefined,
                        goodsMixingFinProdCountermeasureDeadline: data.goodsMixingFinProdCountermeasureDeadline ? moment(data.goodsMixingFinProdCountermeasureDeadline) : null,
                        rawMaterialGoodsMixing: data.rawMaterialGoodsMixing ? Number(data.rawMaterialGoodsMixing) : undefined,
                        rawMatGoodsMixingCountermeasureDeadline: data.rawMatGoodsMixingCountermeasureDeadline ? moment(data.rawMatGoodsMixingCountermeasureDeadline) : null,
                        rawMaterialsIntermediaries: data.rawMaterialsIntermediaries ? Number(data.rawMaterialsIntermediaries) : undefined,
                        rawMaterialsChainTransparency: data.rawMaterialsChainTransparency ? Number(data.rawMaterialsChainTransparency) : undefined,
                        unusualTradeFlows: data.unusualTradeFlows ? Number(data.unusualTradeFlows) : undefined,
                        unusualTradeFlowsCountermeasureDeadline: data.unusualTradeFlowsCountermeasureDeadline ? moment(data.unusualTradeFlowsCountermeasureDeadline) : null
                    };
                    form.setFieldsValue(formattedData);
                })
                .catch(function (error: any) {
                    openNotification('error', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            
            const payload = {
                ...values,
                originOfRelevantPart: dataOriginOfRelevantPart ? dataOriginOfRelevantPart.join(",") : null,
                // Convert country arrays to comma-separated strings
                countryOfProductionOfRelevantPartFinishedProduct: dataCountryOfProductionOfRelevantPartFinishedProduct.join(','),
                countryOfProductionOfRelevantCommodity: dataCountryOfProductionOfRelevantCommodity.join(','),
                originOfRawMat: dataOriginOfRawMat.join(','),
                checkedSyntheticRubber: String(values.checkedSyntheticRubber),
                checkedLegacyStock: String(values.checkedLegacyStock),
                goodsMixing: values.goodsMixing ? String(values.goodsMixing) : undefined,
                rawMaterialOriginIsClear: values.rawMaterialOriginIsClear ? String(values.rawMaterialOriginIsClear) : undefined,
                rawMaterialsContainsWoodPulp: values.rawMaterialsContainsWoodPulp ? String(values.rawMaterialsContainsWoodPulp) : undefined,
                rawMaterialGoodsMixing: values.rawMaterialGoodsMixing ? String(values.rawMaterialGoodsMixing) : undefined,
                rawMaterialsIntermediaries: values.rawMaterialsIntermediaries ? String(values.rawMaterialsIntermediaries) : undefined,
                rawMaterialsChainTransparency: values.rawMaterialsChainTransparency ? String(values.rawMaterialsChainTransparency) : undefined,
                unusualTradeFlows: values.unusualTradeFlows ? String(values.unusualTradeFlows) : undefined,
            };

            if (props.data && props.data.itemFormValues) {
                if (itemId) {
                    // Update the item
                    axiosInstance.current
                        .put(
                            ('/resource/items/' + encodeURIComponent(itemId)),
                            props.data.itemFormValues,
                            config,
                        )
                        .then(() => {
                            // Then save the item data
                            if (axiosInstance.current) {
                                return axiosInstance.current.post(
                                    `/resource/item-data/save-data?itemId=${itemId}`,
                                    payload,
                                    config,
                                );
                            }
                        })
                        .then(() => {
                            openNotification('success', 'Successfully saved.');
                            if (props.onClose) {
                                props.onClose();
                            }
                        })
                        .catch(function (error: any) {
                            openNotification('error', error);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                } else {
                    axiosInstance.current
                        .post(
                            ('/resource/items'),
                            props.data.itemFormValues,
                            config,
                        )
                        .then((response) => {
                            const newId = response.data.id;
                            if (axiosInstance.current) {
                                return axiosInstance.current.post(
                                    `/resource/item-data/save-data?itemId=${newId}`,
                                    payload,
                                    config,
                                );
                            }
                        })
                        .then(() => {
                            openNotification('success', 'Successfully saved.');
                            if (props.onClose) {
                                props.onClose();
                            }
                        })
                        .catch(function (error: any) {
                            openNotification('error', error);
                        })
                        .finally(() => {
                            setLoading(false);
                        });
                }
            } else {

                axiosInstance.current
                    .post(
                        `/resource/item-data/save-data?itemId=${itemId}`,
                        payload,
                        config,
                    )
                    .then(() => {
                        openNotification('success', 'Successfully saved.');
                        if (props.onClose) {
                            props.onClose();
                        }
                    })
                    .catch(function (error: any) {
                        openNotification('error', error);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    }

    const [dataOriginOfRelevantPart, setDataOriginOfRelevantPart] = useState<string[]>([]);
    const [selectedCountryOriginOfRelevantPart, setSelectedCountryOriginOfRelevantPart] = useState<string | null>(null);

    const [dataCountryOfProductionOfRelevantPartFinishedProduct, setDataCountryOfProductionOfRelevantPartFinishedProduct] = useState<string[]>([]);
    const [selectedCountryOfProductionOfRelevantPartFinishedProduct, setSelectedCountryOfProductionOfRelevantPartFinishedProduct] = useState<string | null>(null);

    const [dataCountryOfProductionOfRelevantCommodity, setDataCountryOfProductionOfRelevantCommodity] = useState<string[]>([]);
    const [selectedCountryOfProductionOfRelevantCommodity, setSelectedCountryOfProductionOfRelevantCommodity] = useState<string | null>(null);

    const [dataOriginOfRawMat, setDataOriginOfRawMat] = useState<string[]>([]);
    const [selectedCountryOriginOfRawMat, setSelectedCountryOriginOfRawMat] = useState<string | null>(null);

    const handleAddOriginOfRelevantPart = () => {
        if (!selectedCountryOriginOfRelevantPart) return;

        if (!dataOriginOfRelevantPart.includes(selectedCountryOriginOfRelevantPart)) {
            setDataOriginOfRelevantPart(prev => [...prev, selectedCountryOriginOfRelevantPart]);
        }

        setSelectedCountryOriginOfRelevantPart(null);
    };

    const handleRemoveOriginOfRelevantPart = (itemToRemove: string) => {
        setDataOriginOfRelevantPart(prev => prev.filter(item => item !== itemToRemove));
    };

    const handleAddCountryOfProductionOfRelevantPartFinishedProduct = () => {
        if (!selectedCountryOfProductionOfRelevantPartFinishedProduct) return;

        if (!dataCountryOfProductionOfRelevantPartFinishedProduct.includes(selectedCountryOfProductionOfRelevantPartFinishedProduct)) {
            setDataCountryOfProductionOfRelevantPartFinishedProduct(prev => [...prev, selectedCountryOfProductionOfRelevantPartFinishedProduct]);
        }

        setSelectedCountryOfProductionOfRelevantPartFinishedProduct(null);
    };

    const handleRemoveCountryOfProductionOfRelevantPartFinishedProduct = (itemToRemove: string) => {
        setDataCountryOfProductionOfRelevantPartFinishedProduct(prev => prev.filter(item => item !== itemToRemove));
    };


    const handleAddCountryOfProductionOfRelevantCommodity = () => {
        if (!selectedCountryOfProductionOfRelevantCommodity) return;

        if (!dataCountryOfProductionOfRelevantCommodity.includes(selectedCountryOfProductionOfRelevantCommodity)) {
            setDataCountryOfProductionOfRelevantCommodity(prev => [...prev, selectedCountryOfProductionOfRelevantCommodity]);
        }

        setSelectedCountryOfProductionOfRelevantCommodity(null);
    };

    const handleRemoveCountryOfProductionOfRelevantCommodity = (itemToRemove: string) => {
        setDataCountryOfProductionOfRelevantCommodity(prev => prev.filter(item => item !== itemToRemove));
    };

    const handleAddOriginOfRawMat = () => {
        if (!selectedCountryOriginOfRawMat) return;

        if (!dataOriginOfRawMat.includes(selectedCountryOriginOfRawMat)) {
            setDataOriginOfRawMat(prev => [...prev, selectedCountryOriginOfRawMat]);
        }

        setSelectedCountryOriginOfRawMat(null);
    };

    const handleRemoveOriginOfRawMat = (itemToRemove: string) => {
        setDataOriginOfRawMat(prev => prev.filter(item => item !== itemToRemove));
    };
    return (
        <Form
            layout='vertical'
            form={form}
            onFinish={handleFinish}
        >
            <Spin spinning={loading}>
                <b><Trans>1. I declare, that the parts (final product) I am shipping does not contain EUDR relevant commodity.</Trans></b>
                <Form.Item
                    style={{marginTop: 10, marginBottom: 10}}
                    name="checkedSyntheticRubber"
                    rules={[{required: true, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group
                        onChange={(e) => {
                            setIsSynthetic(e.target.value);
                        }}
                    >
                        <Space direction="horizontal">
                            <Radio value={true}><Trans>Yes</Trans></Radio>
                            <Radio value={false}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider style={{margin: "12px 0"}}/>

                <b><Trans>2. I declare, that the parts (final product) I am shipping is from legacy stock:  created or imported before 30.12.2026. (hereinafter: Legacy Stock)</Trans></b>
                <Form.Item
                    style={{marginTop: 10, marginBottom: 10}}
                    name="checkedLegacyStock"
                    rules={[{required: !isSynthetic, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group disabled={isSynthetic}>
                        <Space direction="horizontal">
                            <Radio value={true}><Trans>Yes</Trans></Radio>
                            <Radio value={false}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider style={{margin: "12px 0"}}/>

                <b>
                    <Trans>3. What is the origin of the relevant product (finished product)?</Trans>
                </b>
                <p>(please provide based on latest LTSD document, or actual invoice)</p>
                <Form.Item
                    style={{marginTop: 10}}
                    name="originOfRelevantPart"
                    rules={[{message: <Trans>Please select a country</Trans>}]}
                >
                    <Select
                        style={{width: "89%", marginRight: 10}}
                        disabled={isSynthetic}
                        showSearch
                        allowClear
                        placeholder={<Trans>Select a country</Trans>}
                        onChange={(value: any) => {
                            setSelectedCountryOriginOfRelevantPart(value);
                        }}
                        optionFilterProp="children"
                    >
                        {countries.map(country => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={handleAddOriginOfRelevantPart}
                    >
                        Add
                    </Button>
                </Form.Item>
                <Form.Item>
                    <List
                        bordered
                        dataSource={dataOriginOfRelevantPart}
                        renderItem={item => (
                            <List.Item>
                                <Typography.Text mark></Typography.Text> {item}
                                <DeleteOutlined
                                    key="delete"
                                    style={{ color: "red", cursor: "pointer", float: "right" }}
                                    onClick={() => handleRemoveOriginOfRelevantPart(item)}
                                />
                            </List.Item>
                        )}
                    />
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>
                <b><Trans>4. Country of production of relevant product (finished product)</Trans></b>
                                <Form.Item
                    style={{marginTop: 20}}

                    name="countryOfProductionOfRelevantPartFinishedProduct"
                    rules={[{message: <Trans>Please select a country</Trans>}]}
                >
                    <Select
                        style={{width: "89%", marginRight: 10}}
                        disabled={isSynthetic}
                        showSearch
                        allowClear
                        placeholder={<Trans>Select a country</Trans>}
                        optionFilterProp="children"
                        onChange={(value: any) => {
                            setSelectedCountryOfProductionOfRelevantPartFinishedProduct(value);
                        }}
                    >
                        {countries.map(country => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={handleAddCountryOfProductionOfRelevantPartFinishedProduct}
                    >
                        Add
                    </Button>
                </Form.Item>
                <Form.Item>
                    <List
                        bordered
                        dataSource={dataCountryOfProductionOfRelevantPartFinishedProduct}
                        renderItem={item => (
                            <List.Item>
                                <Typography.Text mark></Typography.Text> {item}
                                <DeleteOutlined
                                    key="delete"
                                    style={{ color: "red", cursor: "pointer", float: "right" }}
                                    onClick={() => handleRemoveCountryOfProductionOfRelevantPartFinishedProduct(item)}
                                />
                            </List.Item>
                        )}
                    />
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>
                <b><Trans>5. Regio, county, district etc within country of production of relevant product (finished product)</Trans></b>
                                <Form.Item
                    style={{marginTop: 20}}

                    name="regionOfProduction"
                    rules={[{required: !isSynthetic, message: <Trans>Please enter the region</Trans>}]}
                >
                    <Input disabled={isSynthetic} placeholder="regio, county, district etc."/>
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>
                <b><Trans>6. Country of production of relevant commodity/ commodities. Note: Same as  geolocation data</Trans></b>
                                <Form.Item
                    style={{marginTop: 20}}

                    name="countryOfProductionOfRelevantCommodity"
                    rules={[{message: <Trans>Please select a country</Trans>}]}
                >
                    <Select
                        style={{width: "89%", marginRight: 10}}
                        disabled={isSynthetic}
                        showSearch
                        allowClear
                        placeholder={<Trans>Select a country</Trans>}
                        optionFilterProp="children"
                        onChange={(value: any) => {
                            setSelectedCountryOfProductionOfRelevantCommodity(value);
                        }}
                    >
                        {countries.map(country => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={handleAddCountryOfProductionOfRelevantCommodity}
                    >
                        Add
                    </Button>
                </Form.Item>
                <Form.Item>
                    <List
                        bordered
                        dataSource={dataCountryOfProductionOfRelevantCommodity}
                        renderItem={item => (
                            <List.Item>
                                <Typography.Text mark></Typography.Text> {item}
                                <DeleteOutlined
                                    key="delete"
                                    style={{ color: "red", cursor: "pointer", float: "right" }}
                                    onClick={() => handleRemoveCountryOfProductionOfRelevantCommodity(item)}
                                />
                            </List.Item>
                        )}
                    />
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>

                <b><Trans>7. Is it possible to mix relevant part (finished product) with other products originated from negligible or high risk country?</Trans></b>
                <p>(If there is a risk to be mixed, please provide the countermeasure and target date.)</p>
                <Form.Item
                    style={{marginTop: 0, marginBottom: 0}}
                    name="goodsMixing"
                    rules={[{required: !isSynthetic, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group disabled={isSynthetic}
                     onChange={(e) => {
                         setFinProdGoodsMixing(e.target.value === 1);
                     }}
                    >
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    style={{marginTop: 0, marginBottom: 0}}
                    name={["goodsMixingFinProdCountermeasureDeadline"]}
                    rules={[{required: finProdGoodsMixing, message: 'Please provide a date'}]}
                >
                    <DatePicker disabled={isSynthetic || !finProdGoodsMixing} style={{marginTop: 10}}/>
                </Form.Item>
                <Form.Item
                    style={{marginTop: 20, marginBottom: 10}}
                    name={["goodsMixingFinishedProductCountermeasure"]}
                    rules={[{required: finProdGoodsMixing, message: 'Please write a countermeasure'}]}
                >
                    <TextArea disabled={isSynthetic || !finProdGoodsMixing} rows={4}
                              placeholder="Please write the countermeasure here..."/>
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>
                <b><Trans>8. What is the origin of the relevant commodity?</Trans></b>
                                <Form.Item
                    style={{marginTop: 20}}

                    name="originOfRawMat"
                    rules={[{message: <Trans>Please select a country</Trans>}]}
                >
                    <Select
                        style={{width: "89%", marginRight: 10}}
                        disabled={isSynthetic}
                        showSearch
                        allowClear
                        placeholder={<Trans>Select a country</Trans>}
                        optionFilterProp="children"
                        onChange={(value: any) => {
                            setSelectedCountryOriginOfRawMat(value);
                        }}
                    >
                        {countries.map(country => (
                            <Select.Option key={country} value={country}>
                                {country}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        type="primary"
                        onClick={handleAddOriginOfRawMat}
                    >
                        Add
                    </Button>
                </Form.Item>
                <Form.Item>
                    <List
                        bordered
                        dataSource={dataOriginOfRawMat}
                        renderItem={item => (
                            <List.Item>
                                <Typography.Text mark></Typography.Text> {item}
                                <DeleteOutlined
                                    key="delete"
                                    style={{ color: "red", cursor: "pointer", float: "right" }}
                                    onClick={() => handleRemoveOriginOfRawMat(item)}
                                />
                            </List.Item>
                        )}
                    />
                </Form.Item>
                <Divider style={{margin: "12px 0"}}/>

                <b><Trans>9. Is there any risk that different relevant commodities and relevant products (on raw material level) are / could be mixed within the supply chain?</Trans></b>
                <p>(If there is a risk to be mixed, please provide the countermeasure and target date.)</p>
                <Form.Item
                    style={{marginTop: 0, marginBottom: 0}}

                    name="rawMaterialGoodsMixing"
                    rules={[{required: !isSynthetic, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group
                        disabled={isSynthetic}
                        onChange={(e) => {
                            setRawMatGoodsMixing(e.target.value === 1);
                        }}
                    >
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Yes</Trans></Radio>
                            <Radio value={2}><Trans>No</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    style={{marginBottom: 0}}
                    name={["rawMatGoodsMixingCountermeasureDeadline"]}
                    rules={[{required: rawMatGoodsMixing, message: 'Please provide a date'}]}
                >
                    <DatePicker disabled={isSynthetic || !rawMatGoodsMixing} style={{marginTop: 10}}/>
                </Form.Item>
                <Form.Item
                    style={{marginTop: 20, marginBottom: 0}}
                    name={["rawMatGoodsMixingCountermeasure"]}
                    rules={[{required: rawMatGoodsMixing, message: 'Please write a countermeasure'}]}
                >
                    <TextArea disabled={isSynthetic || !rawMatGoodsMixing} rows={4}
                              placeholder="Please write the countermeasure here..."/>
                </Form.Item>
                <Divider></Divider>
                <b><Trans>10. How many  companies are there between the producer of the relevant commodity and you?</Trans></b>
                <Form.Item
                    style={{marginTop: 20, marginBottom: 10}}
                    name="rawMaterialsIntermediaries"
                    rules={[{required: !isSynthetic, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group disabled={isSynthetic}>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>1</Trans></Radio>
                            <Radio value={2}><Trans>2</Trans></Radio>
                            <Radio value={3}><Trans>Multiple</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                <Divider style={{margin: "12px 0"}}/>
                <b>
                    <Trans>11. Is the chain transparent and documented or is transparency compromised?</Trans>
                    <Tooltip
                        placement="top"
                        title={
                            <>
                                <div>
                                    <Trans>
                                        Document Type Relevance Bill of materials (BoM) Shows all input components and
                                        their
                                        origin
                                        Chain-of-custody documentation Helps track material through intermediaries
                                        Certificate of origin Indicates country and possibly region of production
                                        Geolocation map + coordinates Required for raw commodity under EUDR
                                        Transport / shipping docs Prove continuity and timing between origin and
                                        processing
                                        Customs or import/export declarations May confirm declared commodity pathways.
                                    </Trans>
                                </div>
                            </>
                        }
                    >
                        <InfoCircleTwoTone twoToneColor="#1890ff" style={{marginLeft: 5}}/>
                    </Tooltip>
                </b>
                <Form.Item
                    style={{marginTop: 20, marginBottom: 0}}
                    name="rawMaterialsChainTransparency"
                    rules={[{required: !isSynthetic, message: <Trans>Please select an option</Trans>}]}
                >
                    <Radio.Group disabled={isSynthetic}>
                        <Space direction="horizontal">
                            <Radio value={1}><Trans>Transparent and documented</Trans></Radio>
                            <Radio value={2}><Trans>Compromised</Trans></Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <Divider></Divider>
                <b><Trans>12. Have you identified or detected the followings in the supply chain: eg. Unusual trade
                    flows / volume mismatches or any suspicious indicates possible circumvention risk?</Trans></b>
                <p>(If you have identified such risk please provide the countermeasure target date.)</p>
                <Form.Item
                    style={{marginTop: 0, marginBottom: 0}}

                    name={["unusualTradeFlows"]}
                    rules={[{required: !isSynthetic, message: 'Please select an option'}]}
                >
                    <Radio.Group
                        disabled={isSynthetic}
                        onChange={(e) => {
                            setUnusualTradeFlows(e.target.value === 1);
                        }}
                    >
                        <Space direction="horizontal">
                            <Radio value={1}>Yes</Radio>
                            <Radio value={2}>No</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                <Form.Item
                    style={{marginTop: 10, marginBottom: 0}}
                    name={["unusualTradeFlowsCountermeasureDeadline"]}
                    rules={[{required: unusualTradeFlows, message: 'Please provide a date'}]}
                >
                    <DatePicker disabled={isSynthetic || !unusualTradeFlows} style={{marginTop: 10}}/>
                </Form.Item>
                <Form.Item
                    style={{marginTop: 20, marginBottom: 10}}
                    name={["unusualTradeFlowsCountermeasure"]}
                    rules={[{required: unusualTradeFlows, message: 'Please write a countermeasure'}]}
                >
                    <TextArea disabled={isSynthetic || !unusualTradeFlows} rows={4}
                              placeholder="Please write the countermeasure here..."/>
                </Form.Item>
            </Spin>

            <div
                style={{
                    position: "fixed",
                    bottom: 0,
                    right: 0,
                    width: 720,
                    background: "#fff",
                    borderTop: "1px solid #e8e8e8",
                    padding: "10px 20px",
                    zIndex: 100,
                    textAlign: "right"
                }}
            >
                {props.onBack && (
                    <Button
                        htmlType="button"
                        style={{marginRight: 8}}
                        onClick={props.onBack}>
                        <ButtonWithIcon
                            icon={<LeftOutlined/>}
                            text={<Trans>Back</Trans>}
                        />
                    </Button>

                )}
                <Button
                    loading={loading}
                    htmlType="submit"
                    type="primary"
                >
                    <Trans>Save</Trans>
                </Button>
            </div>
        </Form>
    );

}
export default ItemDataForm;
